// ── Connect content policy + AI review (web admin) ───────────────────────────
//
// Typed client for the policy editor (Governance → Connect Content Policy) and the
// "AI review" tab on Social Moderation. Every route lives under /social-moderation
// and is authorised per call on `projectId` — the same moderator check as the rest
// of that page (project-wide data scope + `web.social_moderate`, super admins
// always pass). CHANGING the policy additionally needs `web.social_policy`; a
// moderator without it still reads the policy (GET answers `canEdit: false`) and
// can run tests, and only the PUT answers 403.
//
// Refusals are 403, never 401: the shared axios instance turns any 401 into the
// app-wide "session expired" sign-out (services/api.ts), so "not allowed" must not
// look like "not signed in".
//
// Unlike socialModerationAPI (which hands back raw axios responses), these methods
// UNWRAP the `{ success, data }` envelope and resolve to `data`. Failures still
// reject with the axios error, so callers read `err.response.status` /
// `err.response.data.message` exactly as elsewhere — see connectPolicyErrorStatus /
// connectPolicyErrorMessage below.
//
// Named "Connect content policy" on purpose: "compliance policy" already means the
// store grooming/uniform rules (stores.compliance_policy).

import type { AxiosResponse } from 'axios';
import api from './api';

// ── Policy ───────────────────────────────────────────────────────────────────

/** off = nothing is checked; monitor = checked AFTER publishing, nothing blocked
 *  (the default for a project with no saved policy); enforce = checked BEFORE
 *  publishing, Block rules refuse the post and Hold rules keep it author-only
 *  until a moderator approves it. */
export type PolicyMode = 'off' | 'monitor' | 'enforce';

export type RuleAction = 'block' | 'hold' | 'off';

export interface PolicyRule {
  /** Stable key, `^[a-z0-9_]{2,40}$`, unique within the policy. Stored on every
   *  review row's violations, so it must never be re-derived from the (editable) name. */
  id: string;
  name: string;
  description: string;
  action: RuleAction;
  /** Built-in rules can be switched off but not removed (the server 400s a save
   *  that drops one). */
  builtIn: boolean;
}

export interface ContentPolicy {
  mode: PolicyMode;
  rules: PolicyRule[];
  /** "Always allowed" — overrides the rules (MRP tags, festival greetings, …). */
  allowed: string;
  /** Extra general guidance for the reviewer. */
  guidelines: string;
}

/** A policy as the server holds it. `version` 0 = the built-in default (nothing
 *  saved for this project yet), in which case `isDefault` is true and the
 *  created* fields are null. */
export interface StoredPolicy {
  projectId: number;
  version: number;
  policy: ContentPolicy;
  isDefault: boolean;
  /** Employee code of whoever saved this version. */
  createdBy: string | null;
  /** Display name for `createdBy`, when the server hydrates it. */
  createdByName?: string | null;
  createdAt: string | null;
  note?: string | null;
}

/** GET /policy — everything the editor needs in one round trip. */
export interface ConnectPolicyState {
  policy: StoredPolicy;
  /** The built-in default, for "Reset to default" without a second request. */
  defaultPolicy: ContentPolicy;
  /** Caller may save (super admin or `web.social_policy`). False = read-only editor. */
  canEdit: boolean;
  /** AI review is switched on and configured on this server. When false every
   *  project behaves as mode 'off', whatever is saved. */
  aiAvailable: boolean;
}

export interface PolicyVersionSummary {
  version: number;
  mode: PolicyMode;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string | null;
  note: string | null;
}

// ── Verdicts ─────────────────────────────────────────────────────────────────

export interface Violation {
  ruleId: string;
  /** Name at the time of the check — survives a later rename or removal of the rule. */
  ruleName: string;
  action: 'block' | 'hold';
  confidence: 'high' | 'medium' | 'low';
  /** For moderators. */
  reason: string;
  /** Plain-language instruction for the author. */
  fix: string;
  /** 0-based index into the photos that were checked; null = the text. */
  mediaIndex: number | null;
}

export type AiDecision = 'allow' | 'hold' | 'block' | 'error';

/** What the write path would do right now with this verdict (error → publish:
 *  an AI outage never stops posting). */
export type AiVerdictAction = 'publish' | 'hold' | 'block';

/** The result of POST /policy/test: the AI's verdict plus the action it maps to. */
export interface AiVerdict {
  decision: AiDecision;
  violations: Violation[];
  summary: string;
  model: string | null;
  latencyMs: number;
  error?: string;
  /** 0 = built-in default; -1 = an unsaved draft (a test that sent `policy`). */
  policyVersion: number;
  /** What ENFORCE would do with this verdict, whatever mode the policy is in —
   *  the test panel's "Would publish / hold / block". */
  action: AiVerdictAction;
}

// ── AI review queue ──────────────────────────────────────────────────────────

export type ReviewTargetType = 'post' | 'post_edit' | 'reshare' | 'comment';

/** needs_review = moderator_status 'pending'; monitor = mode monitor AND the AI
 *  would have held or blocked it. */
export type AiReviewFilter = 'needs_review' | 'held' | 'blocked' | 'unchecked' | 'monitor' | 'all';

/** What actually happened to the item. */
export type AiReviewOutcome = 'published' | 'held' | 'blocked' | 'published_unchecked';

export type AiModeratorStatus =
  | 'none' | 'pending' | 'approved' | 'rejected' | 'dismissed' | 'confirmed' | 'overturned' | 'superseded';

/** The item's state NOW, which can differ from `outcome` (a held post since
 *  approved is 'live'; a blocked attempt was never inserted at all). */
export type AiReviewItemState = 'live' | 'held' | 'rejected' | 'removed' | 'author_deleted' | 'never_posted';

export type AiReviewAction = 'approve' | 'reject' | 'dismiss' | 'confirm' | 'overturn';

export interface AiReviewAuthor {
  empCode: string;
  name: string | null;
  designation: string | null;
  storeName: string | null;
}

export interface AiReviewRow {
  /** social_ai_reviews.id (BIGSERIAL). The controller sends it as a number
   *  (Number(x.id)), but int8 is a string in node-pg's default and in other payloads
   *  (e.g. an item's `aiReview`), so it is typed as either. Treat it as opaque —
   *  compare with String(), key with String() — and pass it back as-is: every method
   *  below accepts both forms. */
  id: string | number;
  createdAt: string;
  targetType: ReviewTargetType;
  /** The post (for a comment, its parent post). Null for a blocked NEW post. */
  postId: string | null;
  commentId: string | null;
  author: AiReviewAuthor;
  /** ≤200 chars. */
  contentPreview: string;
  mediaCount: number;
  mode: 'monitor' | 'enforce';
  decision: AiDecision;
  outcome: AiReviewOutcome;
  violations: Violation[];
  summary: string | null;
  moderatorStatus: AiModeratorStatus;
  itemState: AiReviewItemState;
  policyVersion: number | null;
}

export interface AiReviewMedia {
  index: number;
  /** API-relative path (`/social-moderation/ai-reviews/:id/media/:index?projectId=…`).
   *  Needs the bearer, so fetch it through the axios instance (AuthedImage /
   *  socialModerationAPI.getMedia) or use getAiReviewMediaBlob — never an <img src>. */
  url: string;
}

export interface AiReviewDetail extends AiReviewRow {
  content: string | null;
  media: AiReviewMedia[];
  error: string | null;
  model: string | null;
  latencyMs: number | null;
  moderatorEmpCode: string | null;
  moderatorNote: string | null;
  moderatedAt: string | null;
  /** For a comment or a quote-repost: the post it hangs off, for context. */
  parentPostPreview?: string | null;
}

/** Counted over project + date range; the filter pill and search do not narrow them. */
export interface AiReviewCounts {
  checked: number;
  allowed: number;
  held: number;
  blocked: number;
  unchecked: number;
  monitorFlagged: number;
  needsReview: number;
}

export interface AiReviewList {
  rows: AiReviewRow[];
  total: number;
  counts: AiReviewCounts;
}

export interface AiReviewListParams {
  filter?: AiReviewFilter;
  /** YYYY-MM-DD, inclusive. */
  from?: string;
  /** YYYY-MM-DD, inclusive. */
  to?: string;
  q?: string;
  /** ≤200; the server defaults to 50. */
  limit?: number;
  offset?: number;
}

/** The decide endpoint's answer. Only the new status is promised; callers refetch. */
export type AiReviewDecideResult = Partial<Pick<AiReviewRow, 'id' | 'moderatorStatus' | 'itemState'>>;

// ── Helpers ──────────────────────────────────────────────────────────────────

interface Envelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

/** `{ success, data }` → data. A 2xx that still says success:false is treated as a
 *  failure rather than handing the caller `undefined` to render. */
const unwrap = <T>(res: AxiosResponse<Envelope<T>>): T => {
  const body = res.data;
  if (!body || body.success === false || body.data === undefined) {
    throw new Error(body?.message || 'Unexpected response from the server.');
  }
  return body.data;
};

/** HTTP status of a rejected call, or null for a network failure / non-HTTP error. */
export const connectPolicyErrorStatus = (err: unknown): number | null => {
  const status = (err as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : null;
};

/** The server's own message when it sent one (validation errors name the field),
 *  else `fallback`. */
export const connectPolicyErrorMessage = (err: unknown, fallback: string): string => {
  const msg = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (typeof msg === 'string' && msg.trim()) return msg;
  if (connectPolicyErrorStatus(err) === null && err instanceof Error && err.message) return err.message;
  return fallback;
};

/**
 * Parses a server timestamp. TIMESTAMPTZ columns reach the client as raw Postgres
 * text (config/database.ts disables the pg parser), e.g.
 * `2026-09-25 14:23:45.123456+05:30`: a space instead of `T`, microseconds, and
 * sometimes an hour-only offset (`+05`). Chrome forgives all of that; Safari's
 * `new Date()` returns Invalid Date. Normalise to strict ISO first. Epoch millis
 * (number or numeric string) pass straight through.
 */
export const parseServerTimestamp = (value: string | number | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' || /^\d{10,}$/.test(value)) {
    const d = new Date(Number(value));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const iso = value
    .trim()
    .replace(' ', 'T')
    .replace(/(\.\d{3})\d+/, '$1')
    .replace(/([+-]\d{2})$/, '$1:00');
  const d = new Date(iso);
  if (!Number.isNaN(d.getTime())) return d;
  const loose = new Date(value);
  return Number.isNaN(loose.getTime()) ? null : loose;
};

// ── Client ───────────────────────────────────────────────────────────────────

export const connectPolicyAPI = {
  /** The active policy (or the built-in default), plus the default, edit right and
   *  AI availability. 403 = not a moderator for this project. */
  getPolicy: async (projectId: number): Promise<ConnectPolicyState> =>
    unwrap(await api.get('/social-moderation/policy', { params: { projectId } })),

  /** Saves `policy` as the project's next version and makes it active at once.
   *  400 carries a readable validation message; 403 = no `web.social_policy`. */
  savePolicy: async (projectId: number, policy: ContentPolicy, note?: string): Promise<StoredPolicy> =>
    unwrap(await api.put('/social-moderation/policy', {
      projectId,
      policy,
      ...(note && note.trim() ? { note: note.trim() } : {}),
    })),

  /** Saved versions, newest first. Empty = the project has only ever used the default. */
  listVersions: async (projectId: number): Promise<PolicyVersionSummary[]> =>
    unwrap(await api.get('/social-moderation/policy/versions', { params: { projectId } })),

  getVersion: async (projectId: number, version: number): Promise<StoredPolicy> =>
    unwrap(await api.get(`/social-moderation/policy/versions/${encodeURIComponent(String(version))}`, {
      params: { projectId },
    })),

  /**
   * Runs the AI on sample content WITHOUT recording anything. Send `policy` to test
   * an unsaved draft (the server sanitises it exactly as a save would, so an invalid
   * draft 400s with the same message); omit it to test the active policy.
   * `mediaBlobPaths` (≤4) must be social-post uploads — see socialFeedAPI.uploadPhoto.
   * AI unavailable → 200 with decision 'error', not a failure.
   */
  testPolicy: async (
    projectId: number,
    input: { text?: string; mediaBlobPaths?: string[]; policy?: ContentPolicy },
  ): Promise<AiVerdict> =>
    unwrap(await api.post('/social-moderation/policy/test', {
      projectId,
      ...(input.text && input.text.trim() ? { text: input.text } : {}),
      ...(input.mediaBlobPaths && input.mediaBlobPaths.length ? { mediaBlobPaths: input.mediaBlobPaths } : {}),
      ...(input.policy ? { policy: input.policy } : {}),
    })),

  /** One page of AI checks plus the range-wide counts for the chips. */
  listAiReviews: async (projectId: number, params: AiReviewListParams = {}): Promise<AiReviewList> => {
    const q = params.q?.trim();
    return unwrap(await api.get('/social-moderation/ai-reviews', {
      params: {
        projectId,
        ...(params.filter ? { filter: params.filter } : {}),
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
        ...(q ? { q } : {}),
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
        ...(params.offset !== undefined ? { offset: params.offset } : {}),
      },
    }));
  },

  getAiReview: async (projectId: number, id: string | number): Promise<AiReviewDetail> =>
    unwrap(await api.get(`/social-moderation/ai-reviews/${encodeURIComponent(String(id))}`, {
      params: { projectId },
    })),

  /**
   * Photo `index` of a review as a Blob (wrap it in URL.createObjectURL and revoke
   * it when done). Through axios rather than an <img src> because the endpoint needs
   * the bearer — and it serves photos of posts that were never published.
   */
  getAiReviewMediaBlob: async (projectId: number, id: string | number, index: number): Promise<Blob> => {
    const res = await api.get<Blob>(
      `/social-moderation/ai-reviews/${encodeURIComponent(String(id))}/media/${encodeURIComponent(String(index))}`,
      { params: { projectId }, responseType: 'blob' },
    );
    return res.data;
  },

  /**
   * approve / reject a HELD item; dismiss / reject (take down — `note` required) a
   * LIVE flagged one; confirm / overturn a BLOCK (feedback only). `note` on reject
   * is shown to the employee. 409 = someone already decided it.
   */
  decideAiReview: async (
    projectId: number,
    id: string | number,
    action: AiReviewAction,
    note?: string,
  ): Promise<AiReviewDecideResult> => {
    const res = await api.post<Envelope<AiReviewDecideResult>>(
      `/social-moderation/ai-reviews/${encodeURIComponent(String(id))}/decide`,
      { projectId, action, ...(note && note.trim() ? { note: note.trim() } : {}) },
    );
    if (res.data?.success === false) throw new Error(res.data.message || 'Could not save that decision.');
    // The body's data is advisory — callers refetch the list — so a bare
    // `{ success: true }` is a success, not a malformed response.
    return res.data?.data ?? {};
  },
};

export default connectPolicyAPI;
